import { FC } from 'react';
import { Outlet } from 'react-router-dom';
import { NotFoundPage } from 'pages/NotFound';
import FullLoader from 'components/Loader/FullLoader';
import { useBudgetingCapability } from 'hooks/permissions/useBudgetingCapability';

/**
 * Dark-launch gate for the Budget Upload wizard (VDP-1159).
 *
 * Renders the nested route only when the server-authoritative `budgeting`
 * capability is enabled. When it is not — disabled, errored, or a malformed
 * payload (all resolved to `false` by `useBudgetingCapability`) — it renders
 * the app's GENERIC 404 page IN PLACE, at the same URL, with no wizard-specific
 * detail (so a blocked route does not reveal the feature).
 *
 * NOTE: this deliberately does NOT match the app's default behaviour for an
 * unknown URL, which silently redirects to the landing page (the `*` catch-all
 * → Home → `GrabRoutes` → `/project`). A silent redirect hides the failure and
 * bounces the user with no explanation; a blocked route should fail LOUDLY with
 * a plain "not found". We accept that this makes the route distinguishable from
 * other bad URLs — failing loudly is worth more than blending into a silent
 * redirect.
 *
 * While the capability read is in flight it shows the loader, so an enabled
 * wizard never flashes "not found" for a logged-in user.
 *
 * Lives in `components/routes` so it can render `pages/NotFound` without a
 * cross-module page import, and so the same gate can wrap the flag-gated entry
 * point (VDP-1160) when that lands.
 */
export const BudgetingRoute: FC = () => {
  const { isBudgetingEnabled, isLoading } = useBudgetingCapability();

  if (isLoading) {
    return <FullLoader id="budgeting-capability-loader" />;
  }

  if (!isBudgetingEnabled) {
    return <NotFoundPage />;
  }

  return <Outlet />;
};
